/**
 * Piezas del brand kit de La Tablapp, lámina por lámina
 * (`web/assets/Latablapp branding.pdf`, hoja 3: «01. LOGO PRINCIPAL» … «20. ELEMENTO
 * DECORATIVO GRANDE»). Cada componente lleva anotado a qué número del kit responde.
 *
 * Todo es Server Component puro (sin estado ni efectos) y consume los **tokens del
 * sistema** (`--ld-*`), no colores propios: desde que la app adoptó la piel del kit, los
 * valores del sistema y los del kit son los mismos, así que el wrapper ya no re-tintea
 * nada. Un color literal acá sería deriva, no fidelidad al kit.
 *
 * **Esto es el trazo, y el trazo no funciona sobre datos** (FEATURE-100 RF-9): brochazos,
 * garabatos, círculos a mano y marcos son exclusivos de la vitrina. Lo que el kit sí usa
 * dentro de la consola —la trama de semitono, la micro-tipografía, el chip de «en vivo»—
 * vive en [`elementos.tsx`](elementos.tsx). La separación es literal para que importar un
 * brochazo desde una pantalla de datos se vea en el import.
 */
import type { CSSProperties, ReactNode } from "react";
import { ArrowDown, ArrowRight, ArrowUp, Camera, Check, Info, RotateCcw, Share2, Star, Wifi, X, Zap } from "lucide-react";
import { ARROW, BAR_A, BAR_B, BAR_C, FRAME, RING, SCRIBBLE, SPECKS, WEDGE, type Shape } from "@/components/brand/brush";
import { COLOR, Semitono, TEXTO, type Tono } from "@/components/brand/elementos";
import { Isotipo } from "@/components/ui/BrandMark";
import { ACTA, CANCHA_ACCIONES, CANCHA_ULTIMA, FIXTURE, GOLEADORES, TABLA, VITRINA_MOCK } from "@/components/brand/mock";

export function Container({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`mx-auto w-full max-w-[1180px] px-5 sm:px-8 ${className}`}>{children}</div>;
}

export function Eyebrow({ children, tono = "accent" }: { children: ReactNode; tono?: Tono }) {
  return (
    <p className={`font-display text-[11px] uppercase tracking-[0.3em] ${TEXTO[tono]}`}>{children}</p>
  );
}

/** Titular de sección con el brochazo fino de subrayado (kit 06/15). */
export function Titulo({
  children,
  subrayado = "accent",
  className = "",
}: {
  children: ReactNode;
  subrayado?: Tono | "none";
  className?: string;
}) {
  return (
    <h2 className={`font-display relative inline-block text-[clamp(28px,4.6vw,50px)] uppercase leading-[0.95] text-ink ${className}`}>
      {children}
      {subrayado !== "none" && (
        <Brush
          shape={BAR_C}
          fill={COLOR[subrayado]}
          className="absolute -bottom-3 left-0 h-3 w-[62%] opacity-90"
          stretch
        />
      )}
    </h2>
  );
}

/* ══ Trazos (kit 05 · 06 · 14 · 15 · 16 · 20) ══════════════════════════════ */

export function Brush({
  shape,
  fill,
  className = "",
  style,
  stretch = false,
  stroke,
}: {
  shape: Shape;
  fill?: string;
  className?: string;
  style?: CSSProperties;
  stretch?: boolean;
  stroke?: { color: string; width: number };
}) {
  return (
    <svg
      aria-hidden
      viewBox={shape.viewBox}
      preserveAspectRatio={stretch ? "none" : "xMidYMid meet"}
      className={`pointer-events-none ${className}`}
      style={style}
    >
      <path
        d={shape.d}
        fill={stroke ? "none" : fill}
        fillRule="evenodd"
        stroke={stroke?.color}
        strokeWidth={stroke?.width}
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Brochazo grueso con sus motas de cerda (kit 03 · 06). */
export function Brochazo({ tono, className = "", style }: { tono: Tono; className?: string; style?: CSSProperties }) {
  return (
    <svg
      aria-hidden
      viewBox={BAR_A.viewBox}
      preserveAspectRatio="none"
      className={`pointer-events-none ${className}`}
      style={style}
    >
      <path d={BAR_A.d} fill={COLOR[tono]} />
      {SPECKS.map(([x, y, rx, ry], i) => (
        <ellipse key={i} cx={x} cy={y} rx={rx} ry={ry} fill={COLOR[tono]} />
      ))}
    </svg>
  );
}

/** Texto sobre brochazo: el resaltado del banner principal del kit (kit 03). */
export function Resaltado({ children, tono = "accent" }: { children: ReactNode; tono?: Tono }) {
  return (
    <span className="relative isolate inline-block px-[0.28em] py-[0.02em]">
      {/* el brochazo desborda el texto: en las puntas el trazo afina y las letras
          altas del final se salían del verde. El ancho va explícito porque un SVG
          es elemento reemplazado: con left/right y width:auto toma su ancho
          intrínseco (300px) y se come el texto que viene después. */}
      <Brochazo
        tono={tono}
        className="absolute -left-[0.14em] -top-[0.12em] -z-10 h-[124%] w-[calc(100%+0.28em)]"
      />
      {/* el texto sobre el brochazo va limpio: la sombra dura del titular no aplica acá */}
      <span className="relative text-onbright [text-shadow:none]">{children}</span>
    </span>
  );
}

/** Flecha de pincel (kit 05). */
export function Flecha({ tono, className = "", style }: { tono: Tono; className?: string; style?: CSSProperties }) {
  return <Brush shape={ARROW} fill={COLOR[tono]} className={className} style={style} />;
}

/** Garabato blanco (kit 06). */
export function Garabato({ tono = "ink", className = "" }: { tono?: Tono; className?: string }) {
  return <Brush shape={SCRIBBLE} className={className} stroke={{ color: COLOR[tono], width: 5 }} />;
}

/** Círculo trazado a mano alrededor de un número (kit 14). */
export function Circulo({ children, tono, className = "" }: { children: ReactNode; tono: Tono; className?: string }) {
  return (
    <span className={`relative inline-grid h-16 w-16 place-items-center ${className}`}>
      <Brush shape={RING} fill={COLOR[tono]} className="absolute inset-0 h-full w-full" />
      <span className={`font-display relative text-2xl leading-none ${TEXTO[tono]}`}>{children}</span>
    </span>
  );
}

/** Marco trazado a mano (kit 16). */
export function Marco({ children, tono, className = "" }: { children: ReactNode; tono: Tono; className?: string }) {
  return (
    <div className={`relative ${className}`}>
      <Brush
        shape={FRAME}
        fill={COLOR[tono]}
        className="absolute -inset-3 h-[calc(100%+1.5rem)] w-[calc(100%+1.5rem)] opacity-90"
        stretch
      />
      <div className="relative">{children}</div>
    </div>
  );
}

/* ══ Patrones (kit 08) ═════════════════════════════════════════════════════ */

export function Puntos({ tono, className = "" }: { tono: Tono; className?: string }) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none ${className}`}
      style={{
        backgroundImage: `radial-gradient(${COLOR[tono]} 1.7px, transparent 1.8px)`,
        backgroundSize: "15px 15px",
      }}
    />
  );
}

export function Rayas({ tono, className = "" }: { tono: Tono; className?: string }) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none ${className}`}
      style={{
        backgroundImage: `repeating-linear-gradient(45deg, ${COLOR[tono]} 0 3px, transparent 3px 11px)`,
      }}
    />
  );
}

/* ══ Marca (kit 01 · 02) ═══════════════════════════════════════════════════
 * El lockup es el arte original del kit, extraído del PDF a PNG con alfa.
 * El isotipo suelto va como SVG (trazado del mismo original) para poder
 * recolorearlo en usos decorativos.
 * ═══════════════════════════════════════════════════════════════════════════ */

export function Logo({ className = "h-7" }: { className?: string }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element -- static export: <img> plano, sin loader de next/image
    <img
      src="/brand/latablapp-lockup.png"
      alt="La Tablapp"
      className={`w-auto ${className}`}
      width={1600}
      height={199}
    />
  );
}

/* ══ Botones (kit 09) ══════════════════════════════════════════════════════ */

export function Boton({
  children,
  href = "#planes",
  tono = "accent",
  variante = "solido",
  className = "",
}: {
  children: ReactNode;
  href?: string;
  tono?: Tono;
  variante?: "solido" | "contorno";
  className?: string;
}) {
  const base =
    "group inline-flex h-12 items-center justify-center gap-3 rounded-full px-6 font-display text-[13px] uppercase tracking-[0.14em] transition active:scale-[0.97]";
  const estilo: CSSProperties =
    variante === "solido"
      ? { background: COLOR[tono], color: "var(--ld-onbright)" }
      : { borderColor: COLOR[tono], color: COLOR[tono] };

  return (
    <a
      href={href}
      style={estilo}
      className={`${base} ${variante === "solido" ? "hover:brightness-110" : "border-2 hover:bg-white/5"} ${className}`}
    >
      {children}
      <ArrowRight size={16} strokeWidth={3} aria-hidden className="transition group-hover:translate-x-1" />
    </a>
  );
}

/* ══ Etiquetas (kit 10) ════════════════════════════════════════════════════ */

export function Etiqueta({
  children,
  tono = "accent",
  icono = "rayo",
  className = "",
}: {
  children: ReactNode;
  tono?: Tono;
  icono?: "rayo" | "estrella" | "punto";
  className?: string;
}) {
  const oscuro = tono !== "ink";
  return (
    <span
      className={`font-display inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] ${className}`}
      style={{ background: COLOR[tono], color: oscuro ? "var(--ld-onbright)" : "var(--ld-bg)" }}
    >
      {icono === "rayo" && <Zap size={12} fill="currentColor" aria-hidden />}
      {icono === "estrella" && <Star size={12} fill="currentColor" aria-hidden />}
      {icono === "punto" && <span aria-hidden className="h-2 w-2 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/* ══ Pop-up / notificación (kit 11) ════════════════════════════════════════ */

export function Popup({ titulo, texto, className = "" }: { titulo: string; texto: string; className?: string }) {
  return (
    <div className={`w-[260px] rounded-2xl border border-line bg-panel p-4 shadow-[0_24px_60px_-20px_rgb(0_0_0_/_0.9)] ${className}`}>
      <div className="flex items-start gap-3">
        <span aria-hidden className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent text-onbright">
          <Check size={18} strokeWidth={4} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm uppercase leading-none text-ink">{titulo}</p>
          <p className="mt-1.5 text-xs leading-snug text-muted">{texto}</p>
        </div>
        <X size={14} className="mt-0.5 shrink-0 text-muted" aria-hidden />
      </div>
    </div>
  );
}

/* ══ Caja de información (kit 12) ══════════════════════════════════════════ */

export function CajaInfo({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-accent/70 bg-panel p-6 pr-12">
      {/* esquina doblada del kit */}
      <span aria-hidden className="absolute -right-6 -top-6 h-12 w-12 rotate-45 bg-accent" />
      <div className="flex items-start gap-4">
        <span aria-hidden className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent text-onbright">
          <Info size={20} strokeWidth={3} />
        </span>
        <div>
          <p className="font-display text-sm uppercase tracking-[0.1em] text-accent">{titulo}</p>
          <p className="mt-1.5 text-sm leading-relaxed text-muted">{children}</p>
        </div>
      </div>
    </div>
  );
}

/* ══ Barra de progreso (kit 18) ════════════════════════════════════════════ */

export function Barra({ valor, tono, label, dato }: { valor: number; tono: Tono; label: string; dato: string }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-ink/85">{label}</span>
        <span className={`font-display text-lg leading-none ${TEXTO[tono]}`}>{dato}</span>
      </div>
      <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-panel-2">
        <span
          className="v4-grow block h-full rounded-full"
          style={{ width: `${valor}%`, background: COLOR[tono] }}
        />
      </div>
    </div>
  );
}

/* ══ Lista con íconos (kit 19) ═════════════════════════════════════════════ */

export function Lista({ items, tono = "accent", marca = "check" }: { items: string[]; tono?: Tono; marca?: "check" | "equis" }) {
  return (
    <ul className="space-y-3">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-3">
          <span
            aria-hidden
            className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full"
            style={{ background: COLOR[tono], color: tono === "loss" ? "var(--ld-onloss)" : "var(--ld-onbright)" }}
          >
            {marca === "check" ? <Check size={12} strokeWidth={4} /> : <X size={12} strokeWidth={4} />}
          </span>
          <span className="text-sm leading-relaxed text-ink/80">{t}</span>
        </li>
      ))}
    </ul>
  );
}

/* ══ Tarjeta destacada (kit 13) ════════════════════════════════════════════ */

export function TarjetaDestacada({
  etiqueta,
  titulo,
  texto,
  tono,
  icono,
}: {
  etiqueta: string;
  titulo: string;
  texto: string;
  tono: Tono;
  icono: ReactNode;
}) {
  return (
    <article className="relative flex min-h-[210px] flex-col overflow-hidden rounded-2xl border border-line bg-panel p-6">
      <Brush
        shape={WEDGE}
        fill={COLOR[tono]}
        className="absolute -right-2 top-0 h-full w-[46%] opacity-[0.14]"
        stretch
      />
      <Semitono tono={tono} className="absolute inset-y-0 right-0 w-1/2 opacity-25" desde="right" />
      <span
        aria-hidden
        className="relative grid h-11 w-11 place-items-center rounded-full"
        style={{ background: COLOR[tono], color: "var(--ld-onbright)" }}
      >
        {icono}
      </span>
      <p className={`font-display relative mt-5 text-[11px] uppercase tracking-[0.28em] ${TEXTO[tono]}`}>{etiqueta}</p>
      <h3 className="font-display relative mt-2 text-xl uppercase leading-[1.05] text-ink">{titulo}</h3>
      <p className="relative mt-3 flex-1 text-sm leading-relaxed text-muted">{texto}</p>
    </article>
  );
}

/* ══ Mockups del producto ══════════════════════════════════════════════════ */

/** Tabla de posiciones: el color dice la posición (kit, paleta hoja 2). */
export function MockTabla() {
  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-panel">
      <div className="flex items-center justify-between border-b border-line px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <Isotipo className="h-4" />
          <span className="font-display text-sm uppercase tracking-[0.12em] text-ink">Tabla · Fecha 24</span>
        </div>
        <Etiqueta tono="accent2" icono="punto">
          Al día
        </Etiqueta>
      </div>

      <table className="w-full text-left">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.18em] text-muted">
            <th scope="col" className="py-2 pl-4 font-medium">
              #
            </th>
            <th scope="col" className="py-2 font-medium">
              Equipo
            </th>
            <th scope="col" className="py-2 text-center font-medium">
              PJ
            </th>
            <th scope="col" className="py-2 pr-4 text-right font-medium">
              Pts
            </th>
          </tr>
        </thead>
        <tbody>
          {TABLA.map((f, i) => {
            const lider = f.estado === "lider";
            const baja = f.estado === "descenso";
            // corte de la tabla: entre la zona alta y la de descenso va la línea punteada
            const corte = i > 0 && TABLA[i - 1].pos !== f.pos - 1;
            return (
              <tr
                key={f.pos}
                className={`${corte ? "border-t-2 border-dashed border-line" : "border-t border-solid border-line/70"} ${
                  lider ? "bg-accent/12" : baja ? "bg-loss/12" : ""
                }`}
              >
                <td className="relative py-3 pl-4">
                  {(lider || baja) && (
                    <span
                      aria-hidden
                      className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2"
                      style={{ background: lider ? COLOR.accent : COLOR.loss }}
                    />
                  )}
                  <span className={`font-display text-sm ${lider ? "text-accent" : baja ? "text-loss" : "text-muted"}`}>
                    {f.pos}
                  </span>
                </td>
                <td className="py-3">
                  <span className="flex items-center gap-2">
                    {f.mov === "sube" && <ArrowUp size={12} className="text-accent" aria-hidden />}
                    {f.mov === "baja" && <ArrowDown size={12} style={{ color: COLOR.loss }} aria-hidden />}
                    {f.mov === "igual" && <span aria-hidden className="h-[2px] w-3 rounded bg-muted" />}
                    <span className={`truncate text-sm ${lider ? "font-semibold text-ink" : "text-ink/85"}`}>{f.equipo}</span>
                    {lider && (
                      <span className="hidden sm:contents">
                        <Etiqueta tono="accent" icono="estrella">
                          Líder
                        </Etiqueta>
                      </span>
                    )}
                  </span>
                </td>
                <td className="py-3 text-center text-xs text-muted">{f.pj}</td>
                <td className="py-3 pr-4 text-right">
                  <span className={`font-display text-lg ${lider ? "text-accent" : baja ? "text-loss" : "text-ink"}`}>{f.pts}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
        <span className="text-[11px] text-muted">Zona de descenso</span>
        <span className="text-[11px] text-muted">Se recalcula al cerrar cada acta</span>
      </div>
    </div>
  );
}

/** Acta en vivo. */
export function MockActa() {
  return (
    <div className="rounded-2xl border border-line bg-panel p-5">
      <div className="flex items-center justify-between">
        <span
          className="font-display inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.18em]"
          style={{ background: "rgb(220 20 60 / 0.16)", color: COLOR.loss }}
        >
          <span aria-hidden className="v4-live h-1.5 w-1.5 rounded-full" style={{ background: COLOR.loss }} />
          En vivo
        </span>
        <span className="font-display text-xl text-ink">{ACTA.minuto}</span>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <span className="flex-1 text-right text-sm font-semibold text-ink">{ACTA.local.nombre}</span>
        <span className="font-display rounded-xl bg-panel-2 px-4 py-2 text-3xl leading-none text-ink">
          {ACTA.local.goles}–{ACTA.visita.goles}
        </span>
        <span className="flex-1 text-sm font-semibold text-ink">{ACTA.visita.nombre}</span>
      </div>

      <ul className="mt-5 space-y-1.5">
        {ACTA.eventos.map((e) => (
          <li key={e.min} className="flex items-center gap-3 rounded-xl bg-panel-2/70 px-3 py-2">
            <span className="font-display w-8 text-xs text-muted">{e.min}</span>
            <span
              aria-hidden
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: e.tipo === "gol" ? COLOR.accent : "var(--ld-gold)" }}
            />
            <span className="flex-1 text-sm text-ink/80">{e.texto}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Fixture de la fecha, con la barra de avance del kit (18). */
export function MockFixture() {
  return (
    <div className="rounded-2xl border border-line bg-panel p-5">
      <div className="flex items-center justify-between gap-3">
        <span className="font-display text-sm uppercase tracking-[0.12em] text-ink">Fecha 24 · Domingo</span>
        <span className="text-[11px] text-muted">2 de 3 cerradas</span>
      </div>
      <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-panel-2">
        <span className="v4-grow block h-full rounded-full" style={{ width: "66%", background: COLOR.accent }} />
      </div>

      <ul className="mt-4 space-y-2">
        {FIXTURE.map((m) => (
          <li key={m.hora} className="rounded-xl bg-panel-2/70 px-3.5 py-3">
            <div className="flex items-center gap-3">
              <span className="font-display w-11 text-xs text-muted">{m.hora}</span>
              <span className="flex-1 truncate text-sm text-ink/85">
                {m.local} <span className="text-muted">vs</span> {m.visita}
              </span>
              <span
                className="font-display text-base"
                style={{ color: m.estado === "vivo" ? COLOR.accent : m.estado === "final" ? "var(--ld-ink)" : "var(--ld-muted)" }}
              >
                {m.marcador}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 sm:pl-[3.5rem]">
              <span className="text-[11px] text-muted">{m.cancha}</span>
              {m.estado === "vivo" && (
                <span
                  className="font-display inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em]"
                  style={{ background: "rgb(220 20 60 / 0.16)", color: COLOR.loss }}
                >
                  <span aria-hidden className="v4-live h-1 w-1 rounded-full" style={{ background: COLOR.loss }} /> En vivo
                </span>
              )}
              {m.estado === "final" && (
                <span className="font-display rounded-full bg-panel px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-muted">
                  Final
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Goleadores. */
export function MockGoleadores() {
  return (
    <div className="rounded-2xl border border-line bg-panel p-5">
      <span className="font-display text-sm uppercase tracking-[0.12em] text-ink">Goleadores · Serie A</span>
      <ul className="mt-4 space-y-2">
        {GOLEADORES.map((g) => (
          <li key={g.pos} className="flex items-center gap-3 rounded-xl bg-panel-2/70 px-3.5 py-3">
            <span className={`font-display w-5 text-sm ${g.pos === 1 ? "text-accent" : "text-muted"}`}>{g.pos}</span>
            <span className="flex-1">
              <span className="block text-sm font-semibold text-ink">{g.nombre}</span>
              <span className="block text-[11px] text-muted">{g.equipo}</span>
            </span>
            <span className={`font-display text-2xl leading-none ${g.pos === 1 ? "text-accent" : "text-ink/70"}`}>{g.goles}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-center gap-2 rounded-xl border border-dashed border-line px-3.5 py-2.5">
        <Zap size={13} className="shrink-0 text-accent-2" aria-hidden />
        <span className="text-[11px] text-muted">A. Muñoz queda suspendido: 5ª amarilla en la fecha 24.</span>
      </div>
    </div>
  );
}


/* ══ El celular del hincha y el acta del delegado (FEATURE-177) ════════════ */

/**
 * Los dos mockups que sostienen las secciones protagonistas de la landing.
 *
 * `MockHincha` va **pintado con el color de otra liga** (azul, no el acento de marca) a
 * propósito: es la única forma de mostrar que la vista pública es white-label sin
 * escribirlo en un bullet. Es también la razón de que no reutilice `MockTabla`, que va en
 * el acento de La Tablapp.
 *
 * `MockDelegado` muestra el **gesto** —la botonera y el deshacer—, no el resultado. El
 * `MockActa` de más arriba muestra la cronología, que es lo que queda después; acá lo que
 * se vende es que anotar es de un toque y equivocarse no cuesta caro.
 */

/**
 * El acento de la liga del mockup. Deliberadamente NO es el de La Tablapp: la sección de
 * vista pública existe para mostrar que la pantalla del hincha es white-label, y pintarla
 * con el acento de marca destruye lo que viene a demostrar.
 *
 * Son **dos** tonos del mismo azul y no uno, porque el mockup tiene dos fondos opuestos:
 * la cabecera es azul con texto blanco encima —ahí el azul tiene que ser oscuro para
 * llegar a AA—, y la fila del líder es texto azul sobre el fondo oscuro del celular,
 * donde el mismo tono oscuro se hunde. Con un solo azul, uno de los dos lados queda
 * ilegible: `#3b82f6` dejaba el nombre de la liga en 3,68 (mínimo 4,5).
 *
 * Esto no lo mide `npm run test:audit`: esa suite cubre la galería de diseño y auth, no
 * la landing. Se verifica a mano en el navegador si el mockup cambia.
 */
const LIGA = "#2563eb";
const LIGA_CLARO = "#60a5fa";

/* ══ El celular del hincha ═════════════════════════════════════════════════ */

export function MockHincha() {
  const { liga, torneo, vivo } = VITRINA_MOCK;

  return (
    <div className="mx-auto w-[288px] rounded-[2.4rem] border-[6px] border-line bg-panel-2 p-2 shadow-[0_40px_90px_-30px_rgb(0_0_0_/_0.9)]">
      {/* barra de estado: lo que hace que se lea como un celular y no como una tarjeta */}
      <div className="flex items-center justify-between px-3 pb-1.5 pt-1 text-[10px] text-muted">
        <span className="font-display tracking-wider">14:32</span>
        <span aria-hidden className="flex items-center gap-1">
          <Wifi size={11} />
          <span className="h-2 w-4 rounded-[2px] border border-current" />
        </span>
      </div>

      <div className="overflow-hidden rounded-[1.9rem] bg-bg">
        {/* cabecera white-label: escudo y color de la liga, no los de La Tablapp */}
        <div className="px-4 py-3.5" style={{ background: LIGA }}>
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden
              /* escudo blanco con las iniciales en el color de la liga: blanco sobre blanco
                 al 22 % daba 2,70 y no hay tono de azul que lo salve */
              className="font-display grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white text-[11px]"
              style={{ color: LIGA }}
            >
              LQ
            </span>
            <span className="min-w-0">
              <span className="font-display block truncate text-[13px] uppercase leading-tight text-white">{liga}</span>
              <span className="block truncate text-[10px] text-white">{torneo}</span>
            </span>
            <Share2 size={14} aria-hidden className="ml-auto shrink-0 text-white/80" />
          </div>
        </div>

        {/* el partido en vivo, que es lo primero que el hincha busca */}
        <div className="border-b border-line px-4 py-3.5">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-loss/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] text-loss">
            <span aria-hidden className="v4-live h-1 w-1 rounded-full bg-loss" />
            En vivo · {vivo.minuto}
          </span>
          <div className="mt-2.5 flex items-center gap-2">
            <span className="flex-1 truncate text-right text-[11px] font-semibold text-ink">{vivo.local}</span>
            <span className="font-display rounded-lg bg-panel px-2.5 py-1 text-lg leading-none text-ink">{vivo.marcador}</span>
            <span className="flex-1 truncate text-[11px] font-semibold text-ink">{vivo.visita}</span>
          </div>
        </div>

        {/* la tabla, con la fila líder realzada en el color de la liga */}
        <div className="px-3 pb-4 pt-3">
          <p className="font-display px-1 text-[10px] uppercase tracking-[0.18em] text-muted">Tabla de posiciones</p>
          <ul className="mt-2 space-y-1">
            {TABLA.slice(0, 4).map((f) => (
              <li
                key={f.pos}
                className="flex items-center gap-2.5 rounded-lg px-2 py-1.5"
                style={f.estado === "lider" ? { background: `${LIGA}1f` } : undefined}
              >
                <span
                  className="font-display w-3.5 text-[11px]"
                  style={{ color: f.estado === "lider" ? LIGA_CLARO : "var(--ld-muted)" }}
                >
                  {f.pos}
                </span>
                <span className="flex-1 truncate text-[11px] text-ink/85">{f.equipo}</span>
                <span className="w-5 text-right text-[10px] text-muted">{f.pj}</span>
                <span className="font-display w-6 text-right text-[13px] text-ink">{f.pts}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ══ El acta abierta del delegado ══════════════════════════════════════════ */

export function MockDelegado() {
  return (
    <div className="rounded-2xl border border-line bg-panel p-5">
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-loss/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-loss">
          <span aria-hidden className="v4-live h-1.5 w-1.5 rounded-full bg-loss" />
          Acta abierta
        </span>
        <span className="font-display text-2xl leading-none text-ink">{"67'"}</span>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <span className="flex-1 truncate text-right text-sm font-semibold text-ink">Atlético Sur</span>
        <span className="font-display rounded-xl bg-panel-2 px-4 py-2 text-3xl leading-none text-ink">2–1</span>
        <span className="flex-1 truncate text-sm font-semibold text-ink">Puerto FC</span>
      </div>

      {/* la botonera: el gesto que se está vendiendo */}
      <div className="mt-5 grid grid-cols-4 gap-2">
        {CANCHA_ACCIONES.map((a) => (
          <span
            key={a.label}
            className="font-display grid h-14 place-items-center rounded-xl border-2 text-[11px] uppercase tracking-[0.1em]"
            style={{
              borderColor: a.tono === "muted" ? "var(--ld-line)" : `color-mix(in oklab, ${COLOR[a.tono as Tono]} 55%, transparent)`,
              color: a.tono === "muted" ? "var(--ld-muted)" : COLOR[a.tono as Tono],
              background: a.tono === "muted" ? "transparent" : `color-mix(in oklab, ${COLOR[a.tono as Tono]} 12%, transparent)`,
            }}
          >
            {a.label}
          </span>
        ))}
      </div>

      {/* la última jugada, con su deshacer: equivocarse no es caro */}
      <div className="mt-3 flex items-center gap-3 rounded-xl bg-panel-2/70 px-3.5 py-2.5">
        <span className="font-display w-8 shrink-0 text-xs text-muted">{CANCHA_ULTIMA.min}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-ink/85">{CANCHA_ULTIMA.texto}</span>
          <span className="block truncate text-[11px] text-muted">{CANCHA_ULTIMA.equipo}</span>
        </span>
        <span className="font-display inline-flex shrink-0 items-center gap-1.5 rounded-full border border-line px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-muted">
          <RotateCcw size={11} aria-hidden /> Deshacer
        </span>
      </div>

      <div className="mt-3 flex items-center gap-2 rounded-xl border border-dashed border-line px-3.5 py-2.5">
        <Camera size={13} aria-hidden className="shrink-0 text-accent-2" />
        <span className="text-[11px] text-muted">Al cerrar, se adjunta la foto del acta física firmada.</span>
      </div>
    </div>
  );
}

/* ══ Brochazo decorativo grande (kit 20) ═══════════════════════════════════ */

export function DecoracionGrande({ className = "" }: { className?: string }) {
  return (
    <div aria-hidden className={`pointer-events-none ${className}`}>
      {/* cúmulo en diagonal, como el «elemento decorativo grande» del kit: el verde
          y el calipso cruzándose, el semitono detrás y un garabato suelto. El
          contenedor decide dónde se ancla y con cuánta inclinación. */}
      <Semitono tono="ink" className="absolute right-0 top-0 h-24 w-36 opacity-25" desde="right" />
      <Brochazo tono="accent" className="absolute left-0 top-2 h-14 w-[72%] opacity-85" />
      <Brush
        shape={BAR_B}
        fill={COLOR.accent2}
        className="absolute left-[10%] top-12 h-11 w-[64%] opacity-75"
        stretch
      />
      <Garabato className="absolute -top-7 right-[14%] h-8 w-24 -rotate-6 opacity-60" />
    </div>
  );
}
